import { isUnauthorizedView } from '@gem/call/Model/callModel';
import { CallsServerErrorCode } from '@gem/call/Model/callModel.types';
import {
  callB2bPermissionAccessRequestMap,
  callB2bPermissionsRequestStatusRequestMap,
  mapCallB2bDefaultPermissionsRequest,
  mapCallB2bPermissionsRequestsResponse,
  mapCallB2bPermissionsResponse,
  mapParticipantRolesRequest,
  mapPersonalizedPermissionsRequest,
} from '@gem/call/Model/callPermissions/b2b.callPermissions/callPermissionsModel.b2b.mapper';
import { buildCallsModelError } from '@gem/call/Model/error/errorBuilder';
import {
  clearCallPermissionsForMemberRequest as clearUnauthorizedCallPermissionsForMemberRequest,
  createCallPermissionRequestRequest as createUnauthorizedCallPermissionRequestRequest,
  getCallPermissionRequestsRequest as getUnauthorizedCallPermissionRequestsRequest,
  getCallPermissionsRequest as getUnauthorizedCallPermissionsRequest,
  setCallPermissionsForAllRequest as setUnauthorizedCallPermissionsForAllRequest,
  setCallPermissionsForMemberRequest as setUnauthorizedCallPermissionsForMemberRequest,
  updateCallPermissionRequestForAllRequest as updateUnauthorizedCallPermissionRequestForAllRequest,
  updateCallPermissionRequestForMemberRequest as updateUnauthorizedCallPermissionRequestForMemberRequest,
} from '@gem/call/WsApi/unauthorizedWsCallsApi/requests/callRequests';
import {
  clearCallPermissionsForMemberRequest,
  createCallPermissionRequestRequest,
  getCallPermissionRequestsRequest,
  getCallPermissionsRequest,
  setCallParticipantsRolesRequest,
  setCallPermissionsForAllRequest,
  setCallPermissionsForMemberRequest,
  updateCallPermissionRequestForAllRequest,
  updateCallPermissionRequestForMemberRequest,
} from '@gem/call/WsApi/wsCallsApi/requests/callRequests';
import { logger } from '@gem/modules/logger';

import {
  ICallParticipantIdToRole,
  ICallPermissionRequests,
  ICallPermissions,
  ICallPermissionsSettings,
  ICallPersonalizedPermissions,
  PermissionRequestAccess,
  PermissionsRequestStatuses,
} from './callPermissionsModel.types';
import {
  mapCallUnauthorizedPermissionsResponse,
  mapCallUnauthorizedPermissionsRequestsResponse,
  mapCallUnauthorizedDefaultPermissionsRequest,
  mapUnauthorizedPersonalizedPermissionsRequest,
  callUnauthorizedPermissionsRequestStatusRequestMap,
  callUnauthorizedPermissionAccessRequestMap,
} from './unauthorized.callPermissions/callPermissionsModel.unauthorized.mapper';

export const getCallPermissions = async (channelId: string): Promise<ICallPermissionsSettings> => {
  try {
    if (isUnauthorizedView) {
      const result = await getUnauthorizedCallPermissionsRequest({
        channelId,
      });
      if (result.type.case === 'success') {
        return mapCallUnauthorizedPermissionsResponse(result.type.value);
      } else {
        throw new Error('Error');
      }
    }

    const result = await getCallPermissionsRequest({
      channelId,
    });
    if (result.type.case === 'success') {
      return mapCallB2bPermissionsResponse(result.type.value);
    } else {
      throw new Error('Error');
    }
  } catch (error) {
    logger.get('API').error('calls.permissions.getCallPermissions', error);
    throw buildCallsModelError(error);
  }
};

export const getCallPermissionsRequests = async (
  channelId: string
): Promise<ICallPermissionRequests> => {
  try {
    if (isUnauthorizedView) {
      const result = await getUnauthorizedCallPermissionRequestsRequest({
        channelId,
      });

      if (result.type.case === 'success') {
        return mapCallUnauthorizedPermissionsRequestsResponse(result.type.value);
      } else {
        throw new Error('Error');
      }
    }

    const result = await getCallPermissionRequestsRequest({
      channelId,
    });

    if (result.type.case === 'success') {
      return mapCallB2bPermissionsRequestsResponse(result.type.value);
    } else {
      throw new Error('Error');
    }
  } catch (error) {
    logger.get('API').error('calls.permissions.getCallPermissionsRequests', error);
    throw buildCallsModelError(error);
  }
};

export const setCallPermissionsForAll = async (
  channelId: string,
  permissions: Required<ICallPermissions>
): Promise<void> => {
  try {
    if (isUnauthorizedView) {
      await setUnauthorizedCallPermissionsForAllRequest({
        channelId,
        default: mapCallUnauthorizedDefaultPermissionsRequest(permissions),
      });
      return;
    }

    await setCallPermissionsForAllRequest({
      channelId,
      default: mapCallB2bDefaultPermissionsRequest(permissions),
    });
  } catch (error) {
    logger.get('API').error('calls.permissions.setCallPermissionsForAll', error);
    throw buildCallsModelError(error);
  }
};

export const setCallPermissionsForUser = async (
  channelId: string,
  userId: string,
  permissions: ICallPersonalizedPermissions
): Promise<void> => {
  try {
    if (isUnauthorizedView) {
      await setUnauthorizedCallPermissionsForMemberRequest({
        channelId,
        memberId: BigInt(userId),
        personalized: mapUnauthorizedPersonalizedPermissionsRequest(permissions),
      });
      return;
    }

    await setCallPermissionsForMemberRequest({
      channelId,
      memberId: BigInt(userId),
      personalized: mapPersonalizedPermissionsRequest(permissions),
    });
  } catch (error) {
    logger.get('API').error('calls.permissions.setCallPermissionsForUser', error);
    throw buildCallsModelError(error);
  }
};

export const clearCallPermissionsForUser = async (
  channelId: string,
  userId: string
): Promise<void> => {
  try {
    if (isUnauthorizedView) {
      await clearUnauthorizedCallPermissionsForMemberRequest({
        channelId,
        memberId: BigInt(userId),
      });
      return;
    }

    await clearCallPermissionsForMemberRequest({
      channelId,
      memberId: BigInt(userId),
    });
  } catch (error) {
    logger.get('API').error('calls.clearCallPermissionsForUser', error);
  }
};

export const updatePermissionRequestsForAll = async (params: {
  channelId: string;
  status: PermissionsRequestStatuses;
}): Promise<void> => {
  try {
    if (isUnauthorizedView) {
      await updateUnauthorizedCallPermissionRequestForAllRequest({
        channelId: params.channelId,
        status: callUnauthorizedPermissionsRequestStatusRequestMap[params.status],
      });
      return;
    }

    await updateCallPermissionRequestForAllRequest({
      channelId: params.channelId,
      status: callB2bPermissionsRequestStatusRequestMap[params.status],
    });
  } catch (error) {
    logger.get('API').error('calls.updatePermissionRequestsForAll', error);
  }
};

export const updatePermissionRequestForUser = async (params: {
  channelId: string;
  userId: string;
  access: PermissionRequestAccess;
  status: PermissionsRequestStatuses;
}): Promise<void> => {
  try {
    if (isUnauthorizedView) {
      await updateUnauthorizedCallPermissionRequestForMemberRequest({
        channelId: params.channelId,
        memberId: BigInt(params.userId),
        status: callUnauthorizedPermissionsRequestStatusRequestMap[params.status],
        access: callUnauthorizedPermissionAccessRequestMap[params.access],
      });
      return;
    }

    await updateCallPermissionRequestForMemberRequest({
      channelId: params.channelId,
      memberId: BigInt(params.userId),
      status: callB2bPermissionsRequestStatusRequestMap[params.status],
      access: callB2bPermissionAccessRequestMap[params.access],
    });
  } catch (error) {
    logger.get('API').error('calls.updatePermissionRequestForUser', error);
  }
};

export const createPermissionRequest = async (params: {
  channelId: string;
  access: PermissionRequestAccess;
}): Promise<any> => {
  try {
    if (isUnauthorizedView) {
      const response = await createUnauthorizedCallPermissionRequestRequest({
        channelId: params.channelId,
        access: callUnauthorizedPermissionAccessRequestMap[params.access],
      });
      if (response.type.case === 'permissionRequestAlreadySent') {
        throw new Error(CallsServerErrorCode.PermissionAlreadySentError);
      } else if (response.type.case === 'success') {
        return { result: true };
      }
    }

    const response = await createCallPermissionRequestRequest({
      channelId: params.channelId,
      access: callB2bPermissionAccessRequestMap[params.access],
    });
    if (response.type.case === 'permissionRequestAlreadySent') {
      throw new Error(CallsServerErrorCode.PermissionAlreadySentError);
    } else if (response.type.case === 'success') {
      return { result: true };
    }
  } catch (error) {
    logger.get('API').error('calls.createPermissionRequest', error);
    throw buildCallsModelError(error);
  }
};

export const setCallParticipantsRoles = async (
  channelId: string,
  participantIdsToRole: Required<ICallParticipantIdToRole[]>
): Promise<void> => {
  try {
    if (isUnauthorizedView) {
      return;
    }

    const response = await setCallParticipantsRolesRequest({
      channelId,
      participantToRole: mapParticipantRolesRequest(participantIdsToRole),
    });
    if (response.type.case === 'hasNoPermissionsForActionOnCall') {
      throw new Error(CallsServerErrorCode.HasNoPermissionsToPerformAction);
    } else if (response.type.case === 'participantsPerRoleExceeded') {
      throw new Error(CallsServerErrorCode.ParticipantsPerRoleExceeded);
    }
  } catch (error) {
    logger.get('API').error('calls.permissions.setCallParticipantsRoles', error);
    throw buildCallsModelError(error);
  }
};
